import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  FolderOpen, 
  CheckCircle2, 
  Clock, 
  AlertTriangle,
  ArrowRight,
  TrendingUp,
  Euro,
  Banknote,
  TrendingDown,
  Timer,
  Percent,
  Plus,
  Sparkles,
  Bell,
  FileText,
  Upload,
  StickyNote
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { isRestrictedUser } from "./Login";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;

const PROFILE_LABELS = {
  bailleur: "Bailleur",
  proprietaire_occupant: "Propriétaire Occupant",
  sci: "SCI"
};

const STATUS_CONFIG = {
  complete: { label: "Complet", icon: CheckCircle2, color: "text-[hsl(145,100%,39%)]", bg: "bg-[hsl(145,100%,45%)]/10" },
  in_progress: { label: "En cours", icon: Clock, color: "text-[hsl(187,71%,50%)]", bg: "bg-[hsl(187,71%,59%)]/10" },
  incomplete: { label: "Incomplet", icon: AlertTriangle, color: "text-red-500", bg: "bg-red-500/10" }
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const isRestricted = isRestrictedUser();
  const currentUser = localStorage.getItem("label_energie_user") || "";
  const userRole = localStorage.getItem("label_energie_role") || "";

  useEffect(() => {
    // Auto-recalculate on first load for Renova
    if (userRole === "admin_full") {
      fetch(`${API}/recalculate-dashboard`, { method: "POST" }).catch(() => {});
    }
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  const fetchData = async () => {
    try {
      const [statsRes, alertsRes, notifsRes] = await Promise.all([
        axios.get(`${API}/statistics`),
        axios.get(`${API}/dossiers-alerts`),
        axios.get(`${API}/notifications?limit=10&user=${encodeURIComponent(currentUser)}&role=${encodeURIComponent(userRole)}`),
      ]);
      setStats(statsRes.data);
      setAlerts(alertsRes.data.slice(0, 5));
      setNotifications(notifsRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setNotifications(p => ({ ...p, unread_count: 0, notifications: (p.notifications || []).map(n => ({ ...n, read: true })) }));
    } catch (err) { }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 animate-pulse">
        <div className="h-10 bg-muted rounded-xl w-1/3 mb-8"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-36 bg-card rounded-2xl shadow-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  const mprStats = stats?.mpr_stats || { total: 0, revenue: 0, upcoming: 0, marge_realisee: 0, marge_a_venir: 0, avg_days: 0 };
  const stats174 = stats?.stats_174 || { total: 0, revenue: 0, upcoming: 0, marge_realisee: 0, marge_a_venir: 0, avg_days: 0 };
  const statsCumule = stats?.stats_cumule || { total: 0, revenue: 0, upcoming: 0, marge_realisee: 0, marge_a_venir: 0, avg_days: 0 };
  
  const rentaMpr = mprStats.revenue > 0 ? ((mprStats.marge_realisee / mprStats.revenue) * 100).toFixed(1) : 0;
  const renta174 = stats174.revenue > 0 ? ((stats174.marge_realisee / stats174.revenue) * 100).toFixed(1) : 0;
  const rentaGlobal = (stats?.total_revenue || 0) > 0 
    ? (((stats?.total_marge_realisee || 0) / stats.total_revenue) * 100).toFixed(1) 
    : 0;

  return (
    <div className="p-6 md:p-8 space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-foreground">
            Bonjour {localStorage.getItem("label_energie_user") || ""}
          </h1>
          <p className="text-muted-foreground mt-1">
            Vue d'ensemble de vos dossiers clients
          </p>
        </div>
        <Button 
          data-testid="dashboard-new-dossier-btn"
          onClick={() => navigate("/dossiers/new")}
          className="rounded-xl bg-[hsl(145,100%,45%)] hover:bg-[hsl(145,100%,39%)] text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nouveau dossier
        </Button>
      </div>

      {/* Stats Grid - Dossiers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Total Dossiers */}
        <div className="card-floating p-6 group">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-[hsl(200,28%,33%)]/10 dark:bg-[hsl(200,28%,33%)]/30 flex items-center justify-center">
              <FolderOpen className="w-6 h-6 text-[hsl(200,28%,33%)] dark:text-[hsl(187,71%,59%)]" />
            </div>
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Total</span>
          </div>
          <p className="text-4xl font-bold tabular-nums text-foreground" data-testid="stat-total">
            {stats?.total_dossiers || 0}
          </p>
          <div className="flex gap-4 mt-3 text-sm text-muted-foreground">
            <span>MPR: <span className="font-semibold text-foreground">{mprStats.total}</span></span>
            <span>174: <span className="font-semibold text-foreground">{stats174.total}</span></span>
            {statsCumule.total > 0 && <span>Cum.: <span className="font-semibold text-violet-600">{statsCumule.total}</span></span>}
          </div>
        </div>

        {/* Complete */}
        <div className="card-floating p-6 border-l-4 border-l-[hsl(145,100%,45%)]">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-[hsl(145,100%,45%)]/10 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-[hsl(145,100%,39%)]" />
            </div>
            <span className="text-xs font-semibold text-[hsl(145,100%,39%)] uppercase tracking-wider">Complets</span>
          </div>
          <p className="text-4xl font-bold tabular-nums text-[hsl(145,100%,39%)]" data-testid="stat-complete">
            {stats?.complete_dossiers || 0}
          </p>
          <div className="flex gap-4 mt-3 text-sm text-muted-foreground">
            <span>MPR: <span className="font-semibold text-[hsl(145,100%,39%)]">{stats?.mpr_complete || 0}</span></span>
            <span>174: <span className="font-semibold text-[hsl(145,100%,39%)]">{stats?.complete_174 || 0}</span></span>
            {(stats?.complete_cumule || 0) > 0 && <span>Cum.: <span className="font-semibold text-violet-600">{stats?.complete_cumule || 0}</span></span>}
          </div>
        </div>

        {/* In Progress */}
        <div className="card-floating p-6 border-l-4 border-l-[hsl(187,71%,59%)]">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-[hsl(187,71%,59%)]/10 flex items-center justify-center">
              <Clock className="w-6 h-6 text-[hsl(187,71%,50%)]" />
            </div>
            <span className="text-xs font-semibold text-[hsl(187,71%,50%)] uppercase tracking-wider">En cours</span>
          </div>
          <p className="text-4xl font-bold tabular-nums text-[hsl(187,71%,50%)]" data-testid="stat-in-progress">
            {stats?.in_progress_dossiers || 0}
          </p>
          <div className="flex gap-4 mt-3 text-sm text-muted-foreground">
            <span>MPR: <span className="font-semibold text-[hsl(187,71%,50%)]">{stats?.mpr_in_progress || 0}</span></span>
            <span>174: <span className="font-semibold text-[hsl(187,71%,50%)]">{stats?.in_progress_174 || 0}</span></span>
            {(stats?.in_progress_cumule || 0) > 0 && <span>Cum.: <span className="font-semibold text-violet-600">{stats?.in_progress_cumule || 0}</span></span>}
          </div>
        </div>

        {/* Incomplete */}
        <div className="card-floating p-6 border-l-4 border-l-red-500">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <span className="text-xs font-semibold text-red-500 uppercase tracking-wider">Incomplets</span>
          </div>
          <p className="text-4xl font-bold tabular-nums text-red-500" data-testid="stat-incomplete">
            {stats?.incomplete_dossiers || 0}
          </p>
          <div className="flex gap-4 mt-3 text-sm text-muted-foreground">
            <span>MPR: <span className="font-semibold text-red-500">{stats?.mpr_incomplete || 0}</span></span>
            <span>174: <span className="font-semibold text-red-500">{stats?.incomplete_174 || 0}</span></span>
            {(stats?.incomplete_cumule || 0) > 0 && <span>Cum.: <span className="font-semibold text-violet-600">{stats?.incomplete_cumule || 0}</span></span>}
          </div>
        </div>
      </div>

      {/* Financial Stats Grid - Hidden for restricted users */}
      {!isRestricted && (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
        {/* CA Encaissé */}
        <div className="card-floating p-6 border-l-4 border-l-[hsl(145,100%,45%)] bg-emerald-500/10 dark:bg-emerald-900/30">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-[hsl(145,100%,45%)]/20 flex items-center justify-center">
              <Euro className="w-6 h-6 text-[hsl(145,100%,39%)]" />
            </div>
            <span className="text-xs font-semibold text-[hsl(145,100%,39%)] uppercase tracking-wider">CA Encaissé</span>
          </div>
          <p className="text-2xl font-bold tabular-nums text-[hsl(145,100%,39%)]" data-testid="stat-revenue">
            {formatCurrency(stats?.total_revenue)}
          </p>
          <div className="flex flex-col gap-1 mt-3 text-sm">
            <span className="text-muted-foreground">MPR: <span className="font-semibold text-[hsl(145,100%,39%)]">{formatCurrency(mprStats.revenue)}</span></span>
            <span className="text-muted-foreground">174: <span className="font-semibold text-[hsl(145,100%,39%)]">{formatCurrency(stats174.revenue)}</span></span>
            {statsCumule.total > 0 && <span className="text-muted-foreground">Cum.: <span className="font-semibold text-violet-600">{formatCurrency(statsCumule.revenue)}</span></span>}
          </div>
        </div>

        {/* CA à venir */}
        <div className="card-floating p-6 border-l-4 border-l-[hsl(187,71%,59%)] bg-cyan-50 dark:bg-cyan-900/30">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-[hsl(187,71%,59%)]/20 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-[hsl(187,71%,50%)]" />
            </div>
            <span className="text-xs font-semibold text-[hsl(187,71%,50%)] uppercase tracking-wider">CA à venir</span>
          </div>
          <p className="text-2xl font-bold tabular-nums text-[hsl(187,71%,50%)]" data-testid="stat-upcoming">
            {formatCurrency(stats?.total_upcoming_revenue)}
          </p>
          <div className="flex flex-col gap-1 mt-3 text-sm">
            <span className="text-muted-foreground">MPR: <span className="font-semibold text-[hsl(187,71%,50%)]">{formatCurrency(mprStats.upcoming)}</span></span>
            <span className="text-muted-foreground">174: <span className="font-semibold text-[hsl(145,100%,39%)]">{formatCurrency(stats174.upcoming)}</span></span>
            {statsCumule.total > 0 && <span className="text-muted-foreground">Cum.: <span className="font-semibold text-violet-600">{formatCurrency(statsCumule.upcoming)}</span></span>}
          </div>
        </div>

        {/* Marge Réalisée */}
        <div className="card-floating p-6 border-l-4 border-l-purple-500 bg-purple-500/10 dark:bg-purple-900/30">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-800/50 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider">Marge Réalisée</span>
          </div>
          <p className="text-2xl font-bold tabular-nums text-purple-600 dark:text-purple-400" data-testid="stat-marge-realisee">
            {formatCurrency(stats?.total_marge_realisee)}
          </p>
          <div className="flex flex-col gap-1 mt-3 text-sm">
            <span className="text-muted-foreground">MPR: <span className="font-semibold text-purple-600 dark:text-purple-400">{formatCurrency(mprStats.marge_realisee)}</span></span>
            <span className="text-muted-foreground">174: <span className="font-semibold text-purple-600 dark:text-purple-400">{formatCurrency(stats174.marge_realisee)}</span></span>
            {statsCumule.total > 0 && <span className="text-muted-foreground">Cum.: <span className="font-semibold text-violet-600">{formatCurrency(statsCumule.marge_realisee)}</span></span>}
          </div>
        </div>

        {/* Marge à Venir */}
        <div className="card-floating p-6 border-l-4 border-l-amber-500 bg-amber-500/10 dark:bg-amber-900/30">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-800/50 flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <span className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider">Marge à Venir</span>
          </div>
          <p className="text-2xl font-bold tabular-nums text-amber-600 dark:text-amber-400" data-testid="stat-marge-a-venir">
            {formatCurrency(stats?.total_marge_a_venir)}
          </p>
          <div className="flex flex-col gap-1 mt-3 text-sm">
            <span className="text-muted-foreground">MPR: <span className="font-semibold text-amber-600 dark:text-amber-400">{formatCurrency(mprStats.marge_a_venir)}</span></span>
            <span className="text-muted-foreground">174: <span className="font-semibold text-amber-600 dark:text-amber-400">{formatCurrency(stats174.marge_a_venir)}</span></span>
            {statsCumule.total > 0 && <span className="text-muted-foreground">Cum.: <span className="font-semibold text-violet-600">{formatCurrency(statsCumule.marge_a_venir)}</span></span>}
          </div>
        </div>
      </div>
      )}

      {/* Bottom Section - 3 boxes - Hidden for restricted users */}
      {!isRestricted && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-8 mb-16">
        {/* Détail des encaissements */}
        <div className="card-floating p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[hsl(200,28%,33%)]/10 dark:bg-[hsl(200,28%,33%)]/30 flex items-center justify-center">
              <Banknote className="w-5 h-5 text-[hsl(200,28%,33%)] dark:text-[hsl(187,71%,59%)]" />
            </div>
            <h2 className="font-heading font-bold text-lg">Détail des encaissements</h2>
          </div>
          <div className="space-y-4">
            {/* MPR Section */}
            <div className="pb-4 border-b border-border">
              <p className="text-sm font-semibold text-muted-foreground mb-3">Dossiers MPR</p>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">CEE</span>
                  <span className="font-semibold tabular-nums" data-testid="revenue-cee">
                    {formatCurrency(stats?.payments_breakdown?.cee)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">APA</span>
                  <span className="font-semibold tabular-nums" data-testid="revenue-apa">
                    {formatCurrency(stats?.payments_breakdown?.apa)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">MPR</span>
                  <span className="font-semibold tabular-nums" data-testid="revenue-mpr">
                    {formatCurrency(stats?.payments_breakdown?.mpr)}
                  </span>
                </div>
              </div>
            </div>
            {/* 174 Section */}
            <div className="pb-4 border-b border-border">
              <p className="text-sm font-semibold text-[hsl(145,100%,39%)] mb-3">Dossiers 174</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Encaissements 174</span>
                <span className="font-semibold tabular-nums text-[hsl(145,100%,39%)]">
                  {formatCurrency(stats174.revenue)}
                </span>
              </div>
            </div>
            {/* Cumulé Section */}
            {statsCumule.total > 0 && (
            <div className="pb-4 border-b border-border">
              <p className="text-sm font-semibold text-violet-600 mb-3">Dossiers Op. Cumulée</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Encaissements Cumulé</span>
                <span className="font-semibold tabular-nums text-violet-600">
                  {formatCurrency(statsCumule.revenue)}
                </span>
              </div>
            </div>
            )}
            {/* Total */}
            <div className="flex items-center justify-between pt-2">
              <span className="font-bold text-lg">Total</span>
              <span className="font-bold tabular-nums text-xl text-[hsl(145,100%,39%)]">
                {formatCurrency(stats?.total_revenue)}
              </span>
            </div>
          </div>
        </div>

        {/* Taux de complétion */}
        <div className="card-floating p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[hsl(145,100%,45%)]/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-[hsl(145,100%,39%)]" />
            </div>
            <h2 className="font-heading font-bold text-lg">Taux de complétion</h2>
          </div>
          <div className="flex items-end gap-4">
            <p className="text-5xl font-bold tabular-nums font-heading text-foreground" data-testid="completion-rate">
              {stats?.completion_rate || 0}%
            </p>
          </div>
          <Progress 
            value={stats?.completion_rate || 0} 
            className="mt-4 h-3 rounded-full bg-muted"
          />
          <div className="flex gap-6 mt-4 text-sm text-muted-foreground">
            <span>Complets: <span className="font-semibold text-[hsl(145,100%,39%)]">{stats?.complete_dossiers || 0}</span></span>
            <span>Total: <span className="font-semibold text-foreground">{stats?.total_dossiers || 0}</span></span>
          </div>
        </div>

        {/* Rentabilité & Délais */}
        <div className="card-floating p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[hsl(187,71%,59%)]/10 flex items-center justify-center">
              <Percent className="w-5 h-5 text-[hsl(187,71%,50%)]" />
            </div>
            <h2 className="font-heading font-bold text-lg">Rentabilité & Délais</h2>
          </div>
          
          {/* Rentabilité */}
          <div className="mb-6 pb-4 border-b border-border">
            <p className="text-sm font-semibold text-muted-foreground mb-2">Rentabilité moyenne</p>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold tabular-nums text-[hsl(187,71%,50%)]">{rentaGlobal}%</span>
              <span className="text-sm text-muted-foreground">global</span>
            </div>
            <div className="flex gap-6 mt-2 text-sm">
              <span className="text-muted-foreground">MPR: <span className="font-semibold text-foreground">{rentaMpr}%</span></span>
              <span className="text-muted-foreground">174: <span className="font-semibold text-[hsl(145,100%,39%)]">{renta174}%</span></span>
              {statsCumule.total > 0 && <span className="text-muted-foreground">Cum.: <span className="font-semibold text-violet-600">{statsCumule.revenue > 0 ? ((statsCumule.marge_realisee / statsCumule.revenue) * 100).toFixed(1) : 0}%</span></span>}
            </div>
          </div>
          
          {/* Délais */}
          <div>
            <p className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
              <Timer className="w-4 h-4" />
              Délai moyen (jours)
            </p>
            <div className="flex gap-4">
              <div className="flex-1 bg-muted/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold tabular-nums text-foreground">{mprStats.avg_days || 0}</p>
                <p className="text-sm text-muted-foreground mt-1">MPR</p>
              </div>
              <div className="flex-1 bg-[hsl(145,100%,45%)]/10 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold tabular-nums text-[hsl(145,100%,39%)]">{stats174.avg_days || 0}</p>
                <p className="text-sm text-[hsl(145,100%,39%)] mt-1">174</p>
              </div>
              {statsCumule.total > 0 && (
              <div className="flex-1 bg-violet-500/10 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold tabular-nums text-violet-600">{statsCumule.avg_days || 0}</p>
                <p className="text-sm text-violet-600 mt-1">Cum.</p>
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Notifications — always first */}
      {!isRestricted && (
        <div className="card-floating overflow-hidden mt-6" data-testid="notifications-box">
          <div className="p-5 border-b border-border flex items-center justify-between bg-emerald-500/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <Bell className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg">Notifications</h2>
                <p className="text-xs text-muted-foreground">Activité récente</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {(notifications?.unread_count || 0) > 0 && (
                <>
                  <span className="px-2.5 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold" data-testid="notif-count">{notifications.unread_count}</span>
                  <Button variant="outline" size="sm" onClick={markAllRead} className="rounded-lg text-xs">Tout lire</Button>
                </>
              )}
            </div>
          </div>
          <div className="divide-y divide-border">
            {(notifications?.notifications || []).length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Aucune notification</div>
            ) : (
              (notifications.notifications || []).slice(0, 8).map(n => (
                <div key={n.id} className={cn("p-4 flex items-start gap-3 transition-colors", !n.read && "bg-emerald-500/10")} data-testid={`notif-${n.id}`}>
                  <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0",
                    n.type === "document_uploaded" ? "bg-blue-100" : n.type === "dossier_created" ? "bg-indigo-100" : "bg-amber-100"
                  )}>
                    {n.type === "document_uploaded" ? <Upload className="w-4 h-4 text-blue-600" /> :
                     n.type === "dossier_created" ? <FileText className="w-4 h-4 text-indigo-600" /> :
                     <StickyNote className="w-4 h-4 text-amber-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold">{n.message}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {n.created_at ? new Date(n.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}
                      {n.created_by && ` — ${n.created_by}`}
                    </p>
                  </div>
                  {n.dossier_id && (
                    <Button variant="ghost" size="sm" onClick={() => {
                      const isUUID = n.dossier_id.includes("-");
                      navigate(isUUID ? `/dossiers/${n.dossier_id}` : `/etude/${n.dossier_id}`);
                    }} className="rounded-lg text-xs shrink-0">
                      <ArrowRight className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Alerts Section — after notifications */}
      {alerts.length > 0 && (
        <div className="card-floating overflow-hidden mt-6">
          <div className="p-5 border-b border-border flex items-center justify-between bg-red-500/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <h2 className="font-heading font-bold text-lg">Dossiers nécessitant une attention</h2>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate("/dossiers")}
              className="rounded-xl"
              data-testid="view-all-alerts-btn"
            >
              Voir tous
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <div className="divide-y divide-border">
            {alerts.map((dossier, index) => {
              const statusConfig = STATUS_CONFIG[dossier.status] || STATUS_CONFIG.incomplete;
              const StatusIcon = statusConfig.icon;
              const missingDocs = dossier.documents?.filter(d => d.status !== 'validated').length || 0;
              
              return (
                <div 
                  key={dossier.id}
                  className="p-4 flex items-center justify-between hover:bg-muted/50 transition-all cursor-pointer group"
                  onClick={() => navigate(`/dossiers/${dossier.id}`)}
                  data-testid={`alert-dossier-${dossier.id}`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="flex items-center gap-4">
                    <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", statusConfig.bg)}>
                      <StatusIcon className={cn("w-5 h-5", statusConfig.color)} />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground group-hover:text-[hsl(200,28%,33%)] dark:group-hover:text-[hsl(187,71%,59%)] transition-colors">{dossier.client_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {PROFILE_LABELS[dossier.profile]} - {missingDocs} document{missingDocs > 1 ? 's' : ''} manquant{missingDocs > 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-semibold tabular-nums text-foreground">{dossier.progress}%</p>
                      <Progress value={dossier.progress} className="w-24 h-2 mt-1 rounded-full" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-[hsl(145,100%,45%)] group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {stats?.total_dossiers === 0 && (
        <div className="card-floating p-16 text-center">
          <div className="w-20 h-20 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-6">
            <FolderOpen className="w-10 h-10 text-muted-foreground" />
          </div>
          <h3 className="font-heading font-bold text-xl mb-2">Aucun dossier</h3>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            Commencez par créer votre premier dossier client pour suivre vos projets de rénovation énergétique
          </p>
          <Button 
            onClick={() => navigate("/dossiers/new")}
            className="rounded-xl bg-[hsl(145,100%,45%)] hover:bg-[hsl(145,100%,39%)] text-white shadow-lg"
            data-testid="empty-state-new-dossier-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Créer un dossier
          </Button>
        </div>
      )}
    </div>
  );
}
